import React, { useState, useEffect } from 'react'
import { Switch, Spin } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getTPOApprovalConfig, toggleTPOApproval, updateTPOApprovalConfig, buildTPOApprovalPayload } from './actions'
import Button from '../../components/Button'
import { WhitePanel, HeaderDivider } from './style'
import { RightOutlined, DownOutlined } from '@ant-design/icons'
import DownSideArrowIco from '../../components/icons/DownSideArrowIco'
import RightSideArrowIco from '../../../src/components/icons/RightSideArrowIco'
import InfosIcon from '../../components/icons/InfosIcon'
import { Tooltip } from 'antd'
import {
  PageWrapper,
  Header,
  Title,
  ToggleCard,
  ToggleRow,
  ToggleText,
  Description,
  Note,
  Subtitle,
  Divider,
  InfoBox,
  HeaderRow,
  HeaderCol,
  SectionCard,
  SectionTitle,
  SectionContent,
  FieldRow,
  FieldName,
  ToggleCell,
  SectionHeader,
  HeaderCell,
} from './style'

function TPOApproval() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const instituteCampusId = useSelector(
    state => state.auth?.instituteDetails?.id
  )
  const [enabled, setEnabled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [openSection, setOpenSection] = useState([])
  const studentMetrics = useSelector(
  state => state?.institute?.studentsMetrics?.approval_pending_count
)

  const [values, setValues] = useState({}) // all false by default
  const [fetchLoading, setFetchLoading] = useState(true)

  useEffect(() => {
    const fetchConfig = async () => {
      setFetchLoading(true)
      try {
        const data = await dispatch(getTPOApprovalConfig())

        if (data) {
          setEnabled(!!data.isRequestForTPOApproval)

          if (data.tpoApprovalConfig?.fields) {
            const newValues = {}
            const fields = data.tpoApprovalConfig.fields

            Object.keys(fields).forEach(apiKey => {
              const fieldConfig = fields[apiKey]
              const uiKey = findUIKeyForAPIKey(apiKey)

              if (uiKey) {
                newValues[`${uiKey}.editable`] = fieldConfig.requiresApproval
                newValues[`${uiKey}.locked`] = fieldConfig.freezeAfterVerification
              }
            })

            setValues(newValues)
          }
        }
      } catch (error) {
        console.error('Error fetching config:', error)
      } finally {
        setFetchLoading(false)
      }
    }

    fetchConfig()
  }, [dispatch])

  const findUIKeyForAPIKey = (apiKey) => {
    const reverseMapping = {
      'cv': 'personal.CV / Resume Upload',
      'firstName': 'personal.First Name',
      'lastName': 'personal.Last Name',
      'dob': 'personal.Date of Birth',
      'gender': 'personal.Gender',
      'primaryEmail': 'personal.Primary Email Address',
      'phoneNumber': 'personal.Mobile Number',
      'permCountry': 'permanent.Country',
      'permState': 'permanent.State',
      'permCity': 'permanent.City',
      'permPostCode': 'permanent.Pincode',
      'corrCountry': 'Current.Country',
      'corrState': 'Current.State',
      'corrCity': 'Current.City',
      'corrPostCode': 'Current.Pincode',
      'tenth.board': '10thDetails.Board',
      'tenth.endYear': '10thDetails.Year (end)',
      'tenth.marks': '10thDetails.Marks (In %) - If it is cgpa convert it to %',
      'tenth.markSheetUrl': '10thDetails.Uploaded MarkSheet',
      'twelfth.board': '12thDetails.Board',
      'twelfth.endYear': '12thDetails.Year (end)',
      'twelfth.marks': '12thDetails.Marks (In %) - If it is cgpa convert it to %',
      'twelfth.markSheetUrl': '12thDetails.Uploaded MarkSheet',
      'otherEducation.state': 'EducationalDetails.State',
      'otherEducation.city': 'EducationalDetails.City',
      'otherEducation.collegeName': 'EducationalDetails.College Name',
      'otherEducation.university': 'EducationalDetails.University',
      'otherEducation.degree': 'EducationalDetails.Degree',
      'otherEducation.department': 'EducationalDetails.Department',
      'otherEducation.specialisation': 'EducationalDetails.Specialization',
      'otherEducation.endYear': 'EducationalDetails.End Year',
      'otherEducation.marks': 'EducationalDetails.Marks (In %) - If it is cgpa convert it to %',
      'otherEducation.markSheetUrl': 'EducationalDetails.Uploaded MarkSheet',
      'currentCourse.state': 'Pursuing.State',
      'currentCourse.city': 'Pursuing.City',
      'currentCourse.collegeName': 'Pursuing.College Name',
      'currentCourse.university': 'Pursuing.University',
      'currentCourse.degree': 'Pursuing.Degree',
      'currentCourse.department': 'Pursuing.Department',
      'currentCourse.specialisation': 'Pursuing.Specialization',
      'currentCourse.endYear': 'Pursuing.Year of Passing',
      'currentCourse.marks': 'Pursuing.Marks (In %) - If it is cgpa convert it to %',
      'currentCourse.noOfArrears': 'Pursuing.Arrears - Current Arrears/History of Arrears',
      'currentCourse.markSheetUrl': 'Pursuing.Uploaded MarkSheet',
      'projects.title': 'Projects.Project Title',
      'projects.started_in': 'Projects.Start Month',
      // 'projects.startYear': 'Projects.Start Year',
      'projects.ended_in': 'Projects.End Month',
      // 'projects.endYear': 'Projects.End Year',
      'projects.description': 'Projects.Description about the project',
      'projects.certifications': 'Projects.Uploaded Certifications',
      'projects.addNew': 'Projects.Adding New Project',
      'internships.companyName': 'Internships.Company Name',
      'internships.started_in': 'Internships.Start Month',
      // 'internships.startYear': 'Internships.Start Year',
      'internships.ended_in': 'Internships.End Month',
      // 'internships.endYear': 'Internships.End Year',
      'internships.function': 'Internships.Function',
      'internships.role': 'Internships.Role',
      'internships.industry': 'Internships.Industry',
      'internships.description': 'Internships.Description (Roles & Responsibilities)',
      'internships.certifications': 'Internships.Uploaded Certifications',
      'internships.addNew': 'Internships.Adding New Internship',
      'workExperience.companyName': 'WorkExperience.Company Name',
      'workExperience.started_in': 'WorkExperience.Start Month',
      // 'workExperience.startYear': 'WorkExperience.Start Year',
      'workExperience.ended_in': 'WorkExperience.End Month',
      // 'workExperience.endYear': 'WorkExperience.End Year',
      'workExperience.function': 'WorkExperience.Function',
      'workExperience.role': 'WorkExperience.Role',
      'workExperience.industry': 'WorkExperience.Industry',
      'workExperience.description': 'WorkExperience.Description (Roles & Responsibilities)',
      'workExperience.certifications': 'WorkExperience.Uploaded Certifications',
      'workExperience.addNew': 'WorkExperience.Adding New Work Experience',
      'certifications.organisation': 'Certifications.Institute Name',
      'certifications.title': 'Certifications.Course Name',
      'certifications.started_in': 'Certifications.Start Month',
      // 'certifications.startYear': 'Certifications.Start Year',
      'certifications.ended_in': 'Certifications.End Month',
      // 'certifications.endYear': 'Certifications.End Year',
      'certifications.description': 'Certifications.Description about the Certification',
      'certifications.certificate': 'Certifications.Uploaded Certification',
      'certifications.addNew': 'Certifications.Adding New Certification',
      'extraCurriculars': 'ExtraCurricular.Adding New Extracurricular Activities',
    }
    return reverseMapping[apiKey]
  }

const toggleValue = (sectionKey, field, column) => {
  const editableKey = `${sectionKey}.${field}.editable`
  const lockedKey = `${sectionKey}.${field}.locked`

  const isAddressCountry =
    (sectionKey === 'permanent' || sectionKey === 'Current') &&
    field === 'Country'

  const isStartMonth = field === 'Start Month'
  const isStartYear = field === 'Start Year'
  const isEndMonth = field === 'End Month'
  const isEndYear = field === 'End Year'

  const isEducationGroup =
    (sectionKey === 'EducationalDetails' || sectionKey === 'Pursuing') &&
    ['State', 'City', 'College Name'].includes(field)

  setValues(prev => {
    const isTurningOn = !prev[`${sectionKey}.${field}.${column}`]
    let updated = { ...prev }

    // NORMAL toggle behavior
    if (column === 'editable') {
      updated[editableKey] = isTurningOn
      updated[lockedKey] = false
    }

    if (column === 'locked') {
      updated[lockedKey] = isTurningOn
      updated[editableKey] = false
    }

    // 1️⃣ Country → State, City, Pincode
    if (isAddressCountry) {
      ['State', 'City', 'Pincode'].forEach(dep => {
        updated[`${sectionKey}.${dep}.${column}`] = isTurningOn
        updated[`${sectionKey}.${dep}.${column === 'editable' ? 'locked' : 'editable'}`] = false
      })
    }

    // 2️⃣ Start Month ↔ Start Year
    if (isStartMonth || isStartYear) {
      const pair = isStartMonth ? 'Start Year' : 'Start Month'
      updated[`${sectionKey}.${pair}.${column}`] = isTurningOn
      updated[`${sectionKey}.${pair}.${column === 'editable' ? 'locked' : 'editable'}`] = false
    }

    // 3️⃣ End Month ↔ End Year
    if (isEndMonth || isEndYear) {
      const pair = isEndMonth ? 'End Year' : 'End Month'
      updated[`${sectionKey}.${pair}.${column}`] = isTurningOn
      updated[`${sectionKey}.${pair}.${column === 'editable' ? 'locked' : 'editable'}`] = false
    }

    // 4️⃣ EducationalDetails / Pursuing (State ↔ City ↔ College Name)
    if (isEducationGroup) {
      ['State', 'City', 'College Name'].forEach(dep => {
        updated[`${sectionKey}.${dep}.${column}`] = isTurningOn
        updated[`${sectionKey}.${dep}.${column === 'editable' ? 'locked' : 'editable'}`] = false
      })
    }

    return updated
  })
}

  const sections = [
    {
      key: 'personal',
      title: 'Personal Details',
      fields: [
        'CV / Resume Upload',
        'First Name',
        'Last Name',
        'Date of Birth',
        'Gender',
        'Primary Email Address',
        'Mobile Number',
      ],
    },
    {
      key: 'permanent',
      title: 'Permanent Address',
      fields: [
        'Country',
      ],
    },
    {
      key: 'Current',
      title: 'Current Address',
      fields: [
        'Country',
      ],
    },
    {
      key: '10thDetails',
      title: '10th Details',
      fields: [
        'Board',
        'Year (end)',
        'Marks (In %) - If it is cgpa convert it to %',
        'Uploaded MarkSheet',
      ],
    },
    {
      key: '12thDetails',
      title: '12th Details',
      fields: [
        'Board',
        'Year (end)',
        'Marks (In %) - If it is cgpa convert it to %',
        'Uploaded MarkSheet',
      ],
    },
    {
      key: 'EducationalDetails',
      title: 'Past/Other Educational Details',
      fields: [
        'State',
        'University',
        'Degree',
        'Department',
        'Specialization',
        'End Year',
        'Marks (In %) - If it is cgpa convert it to %',
        'Uploaded MarkSheet',
      ],
    },
    {
      key: 'Pursuing',
      title: 'Currently Pursuing',
      fields: [
        'State',
        'University',
        'Degree',
        'Department',
        'Specialization',
        'Year of Passing',
        'Marks (In %) - If it is cgpa convert it to %',
        'Arrears - Current Arrears/History of Arrears',
        'Uploaded MarkSheet',
      ],
    },
    {
      key: 'Projects',
      title: 'Projects',
      fields: [
        'Project Title',
        'Start Month',
        'End Month',
        'Description about the project',
        'Uploaded Certifications',
        'Adding New Project',
      ],
    },
    {
      key: 'Internships',
      title: 'Internships',
      fields: [
        'Company Name',
        'Start Month',
        'End Month',
        'Role',
        'Industry',
        'Description (Roles & Responsibilities)',
        'Uploaded Certifications',
        'Adding New Internship',
      ],
    },
    {
      key: 'WorkExperience',
      title: 'Work Experience',
      fields: [
        'Company Name',
        'Start Month',
        'End Month',
        'Role',
        'Industry',
        'Description (Roles & Responsibilities)',
        'Uploaded Certifications',
        'Adding New Work Experience',
      ],
    },
    {
      key: 'Certifications',
      title: 'Certifications',
      fields: [
        'Institute Name',
        'Course Name',
        'Start Month',
        'End Month',
        'Description about the Certification',
        'Uploaded Certification',
        'Adding New Certification',
      ],
    },
    {
      key: 'ExtraCurricular',
      title: 'Extra Curricular Activities',
      fields: [
        'Adding New Extracurricular Activities'
      ],
    },

  ]

  const handleToggle = async checked => {
    setLoading(true)
    try {
      const result = await dispatch(toggleTPOApproval(checked))
      if (result?.success) {
        setEnabled(checked)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSettings = async () => {
    setSaveLoading(true)
    try {
      const payload = buildTPOApprovalPayload(values)
      await dispatch(updateTPOApprovalConfig(payload))
    } finally {
      setSaveLoading(false)
    }
  }
const DISABLED_FIELDS_BY_SECTION = {
  Pursuing: ['State', 'Degree', 'Department', 'Year of Passing'],
}
const isFieldDisabled = (sectionKey, field) => {
  return DISABLED_FIELDS_BY_SECTION?.[sectionKey]?.includes(field)
}

  return (
    <PageWrapper>
      <Spin spinning={loading} tip="Loading...">
        <WhitePanel style={{ opacity: loading ? 0.5 : 1 }}>
          <Title>Profile Approval Settings</Title>
          {enabled && (
            <Subtitle>
              Configure which student profile fields require review before they
              become active, and which fields should be frozen
            </Subtitle>
          )}

          <Divider />
          <ToggleCard enabled={enabled} style={!enabled ? { backgroundColor: '#EB5D600D' } : {}}>
            <ToggleRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch checked={enabled} onChange={handleToggle} />
                <ToggleText>
                  {enabled
                    ? 'Profile approval notifications enabled'
                    : 'Profile changes approval notifications disabled'}
                </ToggleText>
              </div>

<Button.Primary
  text="View Approval Requests"
  disabled={!enabled && !(studentMetrics > 0)}
  onClick={() => navigate('/tpoApproval/approval-listing')}
  style={{
    backgroundColor:
      enabled || studentMetrics > 0 ? '#3B5BDB' : '#D5D5D5',
    color: '#FFFFFF',
    cursor:
      enabled || studentMetrics > 0 ? 'pointer' : 'not-allowed',
    opacity: 1,
  }}
/>
            </ToggleRow>

            <Description>
              {enabled
                ? 'All profile changes made by students will now be sent here for TPO review and approval. Notifications will be triggered for every update request, whether it is a one-time verification or a repeated edit.'
                : 'Profile approval notifications are disabled. Student profile changes will be applied without TPO review, and no update notifications will be triggered.'}
            </Description>

            <Note>
              <b>Note:</b> Change the profile settings to switch fields between
              Editable (With Approval) and Locked After Verification.
            </Note>

          </ToggleCard>
          {enabled && (
            <Header>
              <InfoBox>
                Changes made here affect future student edits only. Existing pending
                requests and previously approved values are not modified by these
                settings.
              </InfoBox>
              <Button.Primary
                text="Save Settings"
                onClick={handleSaveSettings}
                loading={saveLoading}
                disabled={saveLoading}
                style={{
                  display: 'inline-flex',
                  flexDirection: 'row',
                  marginLeft: '90px',
                  alignItems: 'left',
                  gap: '10px',
                  width: 'auto',
                  minHeight: '38px',
                  padding: '9px 20px',
                  borderRadius: '10px',
                }}
              />
            </Header>
          )}
          {enabled && (
            <HeaderRow>
              <HeaderCol flex={2.4}>PROFILE FIELDS</HeaderCol>
              <HeaderCol style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Tooltip title="Student edits need approval. TPO can edit anytime." placement='bottom'>
                  <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
                    <InfosIcon />
                  </span>
                </Tooltip>
                EDITABLE (WITH APPROVAL)
              </HeaderCol>
              <HeaderCol style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Tooltip title="These fields stay locked for the students after verification. TPO can edit anytime." placement='bottom'>
                  <span style={{ display: 'inline-flex', cursor: 'pointer' }}>
                    <InfosIcon />
                  </span>
                </Tooltip>
                LOCKED AFTER VERIFICATION
              </HeaderCol>
            </HeaderRow>
          )}
          {enabled &&
            sections.map(section => {
              const isOpen = Array.isArray(openSection)
                ? openSection.includes(section.key)
                : openSection === section.key

              return (
                <SectionCard key={section.key}>
                  <SectionHeader onClick={() => {
                    setOpenSection(prev => {
                      if (!Array.isArray(prev)) return [section.key]

                      return prev.includes(section.key)
                        ? prev.filter(k => k !== section.key)
                        : [...prev, section.key]
                    })
                  }}>
                    <SectionTitle>{section.title}</SectionTitle>
                    {isOpen ? <DownSideArrowIco /> : <RightSideArrowIco />}
                  </SectionHeader>

                  {isOpen && (
                    <SectionContent>
                      {section.fields.map(field => {
                        const editableKey = `${section.key}.${field}.editable`
                        const lockedKey = `${section.key}.${field}.locked`
                        const getAddressGroup = (sectionKey, field) => {
                          if (
                            (sectionKey === 'permanent' || sectionKey === 'Current') &&
                            field === 'Country'
                          ) {
                            return ['Country', 'State', 'City', 'Pincode']
                          }
                          return null
                        }

                        const getGroupedFields = (sectionKey, field) => {
                          if (
                            (sectionKey === 'EducationalDetails' || sectionKey === 'Pursuing') &&
                            field === 'State'
                          ) {
                            return ['State', 'City', 'College Name']
                          }

                          return null
                        }

                        const splitMonthYear = field => {
                          if (field.startsWith('Start ')) {
                            return ['Start Month', 'Start Year']
                          }

                          if (field.startsWith('End ')) {
                            return ['End Month', 'End Year']
                          }

                          return null
                        }
                        const groupedFields = getGroupedFields(section.key, field)

                        if (groupedFields) {
                          return (
                            <FieldRow key={`${section.key}-grouped`}>
                              <FieldName>
                                <div style={{ display: 'flex', gap: '24px' }}>
                                  {groupedFields.map(part => (
                                    <span key={part}>{part}</span>
                                  ))}
                                </div>
                              </FieldName>

                              <ToggleCell>
                                <Switch
                                  checked={!!values[`${section.key}.${field}.editable`]}
                                  disabled={isFieldDisabled(section.key, field)}
                                  onChange={() => toggleValue(section.key, field, 'editable')}
                                />
                              </ToggleCell>

                              <ToggleCell>
                                <Switch
                                  checked={!!values[`${section.key}.${field}.locked`]}
                                  disabled={isFieldDisabled(section.key, field)}
                                  onChange={() => toggleValue(section.key, field, 'locked')}
                                />
                              </ToggleCell>
                            </FieldRow>
                          )
                        }
const addressGroup = getAddressGroup(section.key, field)

if (addressGroup) {
  return (
    <FieldRow key={`${section.key}-address`}>
      <FieldName>
        <div style={{ display: 'flex', gap: '24px' }}>
          {addressGroup.map(item => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </FieldName>

      <ToggleCell>
        <Switch
          checked={!!values[`${section.key}.${field}.editable`]}
          onChange={() => toggleValue(section.key, field, 'editable')}
        />
      </ToggleCell>

      <ToggleCell>
        <Switch
          checked={!!values[`${section.key}.${field}.locked`]}
          onChange={() => toggleValue(section.key, field, 'locked')}
        />
      </ToggleCell>
    </FieldRow>
  )
}

                        const isAddressField =
                          (section.key === 'permanent' || section.key === 'Current') &&
                          field === 'Country State City Pincode'

                        const monthYearParts = splitMonthYear(field)

                        if (isAddressField) {
                          const parts = field.split(' ')

                          return (
                            <FieldRow key={field}>
                              <FieldName>
                                <div style={{ display: 'flex', gap: '24px' }}>
                                  {parts.map(part => (
                                    <span key={part}>{part}</span>
                                  ))}
                                </div>
                              </FieldName>
                              <ToggleCell>
                                <Switch
                                  checked={!!values[editableKey]}
                                  disabled={isFieldDisabled(section.key, field)}
                                  onChange={() => toggleValue(section.key, field, 'editable')}
                                />
                              </ToggleCell>
                              <ToggleCell>
                                <Switch
                                  checked={!!values[lockedKey]}
                                  disabled={isFieldDisabled(section.key, field)}
                                  onChange={() => toggleValue(section.key, field, 'locked')}
                                />
                              </ToggleCell>
                            </FieldRow>
                          )
                        }

                        if (monthYearParts) {
                          return (
                            <FieldRow key={field}>
                              <FieldName>
                                <div style={{ display: 'flex', gap: '24px' }}>
                                  {monthYearParts.map(part => (
                                    <span key={part}>{part}</span>
                                  ))}
                                </div>
                              </FieldName>

                              <ToggleCell>
                                <Switch
                                  checked={!!values[editableKey]}
                                  disabled={isFieldDisabled(section.key, field)}
                                  onChange={() => toggleValue(section.key, field, 'editable')}
                                />
                              </ToggleCell>

                              <ToggleCell>
                                <Switch
                                  checked={!!values[lockedKey]}
                                  disabled={isFieldDisabled(section.key, field)}
                                  onChange={() => toggleValue(section.key, field, 'locked')}
                                />
                              </ToggleCell>
                            </FieldRow>
                          )
                        }



                        return (
                          <FieldRow key={field}>
                            <FieldName>{field}</FieldName>

                            <ToggleCell>
                              <Switch
                                checked={!!values[editableKey]}
                                disabled={isFieldDisabled(section.key, field)}
                                onChange={() => toggleValue(section.key, field, 'editable')}
                              />
                            </ToggleCell>

                            <ToggleCell>
                              <Switch
                                checked={!!values[lockedKey]}
                                disabled={isFieldDisabled(section.key, field)}
                                onChange={() => toggleValue(section.key, field, 'locked')}
                              />
                            </ToggleCell>
                          </FieldRow>
                        )
                      })}
                    </SectionContent>

                  )}
                </SectionCard>

              )
            })}
        </WhitePanel>
      </Spin>
    </PageWrapper>
  )
}

export default TPOApproval